import { useTranslation } from "@/i18n";
import { Sparkles } from "lucide-react";
import { useState } from "react";

/**
 * Props for the ActionButtons component.
 */
interface ActionButtonsProps {
  /** Whether any operation is in progress */
  readonly busy: boolean;
  /** Whether a re-run operation is in progress */
  readonly rerunning: boolean;
  /** Whether the proceed operation is in progress */
  readonly proceeding: boolean;
  /** Whether data is currently streaming */
  readonly streaming: boolean;
  /** Whether user has a valid Microsoft account */
  readonly hasMicrosoftAccount: boolean;
  /** Callback to re-run the analysis */
  readonly onRerun: () => void;
  /** Callback to proceed to next step */
  readonly onProceed: () => void;
  /** Callback to go back to file selection */
  readonly onBack: () => void;
}

/**
 * ActionButtons component displays the action buttons at the bottom of the review.
 * Includes "Neu analysieren", "Zurück", and "Weiter" buttons.
 * Shows a tooltip when Microsoft account is required but not present.
 *
 * @param props - Component props
 * @returns Action buttons UI
 */
export default function ActionButtons({
  busy,
  rerunning,
  proceeding: _proceeding,
  streaming,
  hasMicrosoftAccount,
  onRerun,
  onProceed,
  onBack,
}: ActionButtonsProps) {
  const { t } = useTranslation();
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div className="flex justify-between items-center pt-4 border-t border-[#e4e4e7] mt-4">
      {/* Neu analysieren button */}
      <button
        type="button"
        onClick={onRerun}
        disabled={rerunning || busy || streaming}
        className="flex items-center gap-2 px-3 py-1.5 bg-white border border-[#e4e4e7] text-[#18181b] text-sm rounded-lg hover:bg-[#f4f4f5] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
      >
        <Sparkles className="w-4 h-4" />
        {rerunning ? t("common.buttons.reanalyzing") : t("common.buttons.reanalyze")}
      </button>

      {/* Right buttons */}
      <div className="flex items-center gap-6">
        <button
          type="button"
          onClick={onBack}
          className="px-3 py-1.5 bg-white border border-[#e4e4e7] text-[#18181b] text-sm rounded-lg hover:bg-[#f4f4f5] cursor-pointer"
        >
          {t("common.buttons.back")}
        </button>
        <span
          className="relative"
          onMouseEnter={() => !hasMicrosoftAccount && setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <button
            type="button"
            onClick={onProceed}
            disabled={busy || !hasMicrosoftAccount}
            className={`px-3 py-1.5 text-sm font-semibold rounded-lg ${
              hasMicrosoftAccount
                ? "bg-[#8e2bf0] text-white hover:bg-[#8e2bf0] cursor-pointer"
                : "bg-[#e4e4e7] text-[#a1a1aa] cursor-not-allowed"
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {t("common.buttons.continue")}
          </button>
          {!hasMicrosoftAccount && showTooltip && (
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-[#27272a] text-white text-xs rounded shadow-lg whitespace-nowrap z-10">
              {t("documentUpload.selection.accountRequired")}
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-[#27272a]"></div>
            </div>
          )}
        </span>
      </div>
    </div>
  );
}
